// AICA Tooltip Component - Meridian Dark Design System
// Contextual help with smart positioning
// Bloomberg Terminal style: Subtle, professional, helpful

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipRoot = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipArrow = TooltipPrimitive.Arrow;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      // Base styles - Meridian Dark specifications
      'z-popover overflow-hidden rounded-sm px-3 py-2 text-sm',
      // Colors matching Meridian Dark
      'bg-bg-elevated border border-border shadow-md',
      'text-fg-secondary',
      // Max width constraint
      'max-w-[300px]',
      // Animations - delayed-open with specific timing
      'data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95',
      'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
      // Slide animations based on side
      'data-[side=bottom]:slide-in-from-top-2',
      'data-[side=left]:slide-in-from-right-2',
      'data-[side=right]:slide-in-from-left-2',
      'data-[side=top]:slide-in-from-bottom-2',
      className
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/**
 * Simple Tooltip component (most common use case)
 * Meridian Dark Design System compliant
 *
 * Features:
 * - 200ms delay on show, 0ms on hide
 * - Auto-placement with collision detection
 * - ARIA describedby for screen readers
 * - Keyboard accessible (focus shows tooltip)
 * - Max width of 300px
 *
 * @example
 * ```tsx
 * <Tooltip content="This is a helpful tooltip">
 *   <Button>Hover me</Button>
 * </Tooltip>
 * ```
 */
export interface TooltipProps {
  /**
   * Tooltip content (text or JSX)
   */
  content: React.ReactNode;
  /**
   * Element to trigger tooltip
   */
  children: React.ReactElement;
  /**
   * Side of trigger to show tooltip
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Alignment of tooltip
   */
  align?: 'start' | 'center' | 'end';
  /**
   * Delay before showing (ms) - defaults to 200ms per Meridian specs
   */
  delayDuration?: number;
  /**
   * Show arrow pointing to trigger
   */
  showArrow?: boolean;
  /**
   * Additional className for content
   */
  className?: string;
  /**
   * Disable tooltip on touch devices
   */
  disableOnTouch?: boolean;
}

export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  delayDuration = 200, // Meridian spec: 200ms show delay
  showArrow = true, // Default to show arrow per spec
  className,
  disableOnTouch = true, // Disable on touch devices by default
}: TooltipProps) {
  // Check if device is touch-capable
  const [isTouchDevice, setIsTouchDevice] = React.useState(false);

  React.useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  // Don't render tooltip on touch devices if disabled
  if (disableOnTouch && isTouchDevice) {
    return children;
  }

  return (
    <TooltipRoot delayDuration={delayDuration}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipPrimitive.Portal>
        <TooltipContent
          side={side}
          align={align}
          className={className}
          collisionPadding={8}
          avoidCollisions={true}
        >
          {content}
          {showArrow && (
            <TooltipArrow
              className="fill-bg-elevated stroke-border"
              width={11}
              height={5}
            />
          )}
        </TooltipContent>
      </TooltipPrimitive.Portal>
    </TooltipRoot>
  );
}

/**
 * Info Tooltip (with icon)
 * Shows an info icon that triggers tooltip
 * Meridian Dark styled
 *
 * @example
 * ```tsx
 * <div className="flex items-center gap-2">
 *   <label>Budget Limit</label>
 *   <InfoTooltip content="Maximum monthly spend for AI providers" />
 * </div>
 * ```
 */
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface InfoTooltipProps {
  /**
   * Tooltip content
   */
  content: React.ReactNode;
  /**
   * Icon size
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Side of trigger to show tooltip
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Additional className for trigger
   */
  className?: string;
}

export function InfoTooltip({
  content,
  size = 'sm',
  side = 'top',
  className,
}: InfoTooltipProps) {
  const { t } = useTranslation();
  const sizeClasses = {
    sm: 'h-3.5 w-3.5',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  return (
    <Tooltip content={content} side={side} showArrow>
      <button
        type="button"
        className={cn(
          'inline-flex items-center justify-center text-fg-muted hover:text-fg transition-all duration-150',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          'focus-visible:ring-offset-bg-base',
          'rounded-full',
          className
        )}
        aria-label={t('common:common.moreInformation')}
      >
        <Info className={sizeClasses[size]} />
        <span className="sr-only">{t('common:common.moreInformation')}</span>
      </button>
    </Tooltip>
  );
}

/**
 * Keyboard Shortcut Tooltip
 * Shows keyboard shortcut hint with proper styling
 * Meridian Dark themed
 *
 * @example
 * ```tsx
 * <KeyboardTooltip shortcut="⌘K">
 *   <Button>Search</Button>
 * </KeyboardTooltip>
 * ```
 */
export interface KeyboardTooltipProps {
  /**
   * Keyboard shortcut to display
   */
  shortcut: string;
  /**
   * Optional description
   */
  description?: string;
  /**
   * Element to trigger tooltip
   */
  children: React.ReactElement;
  /**
   * Side of trigger to show tooltip
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
}

export function KeyboardTooltip({
  shortcut,
  description,
  children,
  side = 'bottom',
}: KeyboardTooltipProps) {
  return (
    <Tooltip
      content={
        <div className="flex items-center gap-2">
          {description && <span className="text-fg-secondary">{description}</span>}
          <kbd className="px-1.5 py-0.5 text-xs font-mono bg-bg-surface border border-border rounded">
            {shortcut}
          </kbd>
        </div>
      }
      side={side}
      delayDuration={500}
    >
      {children}
    </Tooltip>
  );
}

/**
 * Rich Tooltip (with title and body)
 * More detailed tooltip content with Meridian Dark styling
 *
 * @example
 * ```tsx
 * <RichTooltip
 *   title="Quality Score"
 *   body="AI-powered analysis of conversation quality across 7 dimensions (A-G)"
 * >
 *   <Badge>Score: 8.2</Badge>
 * </RichTooltip>
 * ```
 */
export interface RichTooltipProps {
  /**
   * Tooltip title
   */
  title: string;
  /**
   * Tooltip body text
   */
  body: string;
  /**
   * Optional footer (e.g., link, action)
   */
  footer?: React.ReactNode;
  /**
   * Element to trigger tooltip
   */
  children: React.ReactElement;
  /**
   * Side of trigger to show tooltip
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Max width of tooltip (default 300px per Meridian spec)
   */
  maxWidth?: number;
}

export function RichTooltip({
  title,
  body,
  footer,
  children,
  side = 'top',
  maxWidth = 300, // Meridian spec: 300px max width
}: RichTooltipProps) {
  return (
    <TooltipRoot delayDuration={200}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipPrimitive.Portal>
        <TooltipContent
          side={side}
          className="p-0"
          style={{ maxWidth }}
          collisionPadding={8}
          avoidCollisions={true}
        >
          <div className="p-3">
            <h4 className="text-sm font-semibold text-fg mb-1">{title}</h4>
            <p className="text-xs text-fg-secondary leading-relaxed">{body}</p>
            {footer && (
              <div className="mt-2 pt-2 border-t border-border text-xs">{footer}</div>
            )}
          </div>
          <TooltipArrow
            className="fill-bg-elevated stroke-border"
            width={11}
            height={5}
          />
        </TooltipContent>
      </TooltipPrimitive.Portal>
    </TooltipRoot>
  );
}

/**
 * Tooltip Provider
 * Wrap your app or a section with this to enable tooltips
 * Provides global delay settings for all tooltips
 *
 * @example
 * ```tsx
 * <TooltipProvider>
 *   <App />
 * </TooltipProvider>
 * ```
 */
export interface TooltipProviderProps {
  children: React.ReactNode;
  /**
   * Global delay before showing tooltips (ms)
   */
  delayDuration?: number;
  /**
   * Global skip delay duration (ms)
   */
  skipDelayDuration?: number;
  /**
   * Disable hover card style tooltips on hover
   */
  disableHoverableContent?: boolean;
}

export function TooltipProviderWrapper({
  children,
  delayDuration = 200,
  skipDelayDuration = 0,
  disableHoverableContent = false,
}: TooltipProviderProps) {
  return (
    <TooltipProvider
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      disableHoverableContent={disableHoverableContent}
    >
      {children}
    </TooltipProvider>
  );
}

// Export primitives for advanced use cases
export {
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
  TooltipArrow,
  TooltipProviderWrapper as TooltipWrapper,
};
